import { color, Icon, useBreakpoint, media } from "@sencrop/ui-components";
import { WebText, webtext, WebTitle } from "@sencrop/ui-web";
import { useStaticQuery, graphql } from "gatsby";
import React from "react";
import styled, { css } from "styled-components";
import BackgroundElement from "../components/BackgroundElement";
import { containerCss } from "../helpers/style";
import ImageElement from "../components/ImageElement";
import VideoElement from "../components/VideoElement";
import ButtonElement from "../containers/ButtonElement";
import PartnerItem from "../components/PartnerItem";
import { Position } from "../utils/types";

const SectionHeroBlue = ({
  title,
  description,
  button,
  textArguments: _arguments,
  image,
  imageAboveText,
  videoUrl,
  extraPaddingBottom,
}: GatsbyTypes.SectionHeroBlueFragmentFragment) => {
  const onMobileOrTablet = useBreakpoint("mobile", "tablet");

  const data = useStaticQuery<GatsbyTypes.HeroBlueImageQuery>(graphql`
    query HeroBlueImage {
      file(relativePath: { eq: "hero/blue-background.jpg" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, placeholder: NONE, layout: FULL_WIDTH)
        }
      }
    }
  `);

  return (
    <Background $extraPaddingBottom={extraPaddingBottom}>
      <BackgroundWrapper
        $fullHeight={(imageAboveText || (!image && !videoUrl)) ?? false}
      >
        <BackgroundElement
          backgroundPosition="center top"
          backgroundImage={data.file?.childImageSharp?.gatsbyImageData}
          color="blue"
        ></BackgroundElement>
      </BackgroundWrapper>
      <Wrapper $imageAboveText={imageAboveText ?? false}>
        {image && !videoUrl && imageAboveText && (
          <PartnerItem partner={{ logo: image }} encapsulatedLogo />
        )}
        <Container align={"center"}>
          <Header>
            <WebTitle
              size={onMobileOrTablet ? "l" : "xxl"}
              as="h1"
              color="light"
            >
              {title}
            </WebTitle>
            {description?.childMarkdownRemark?.html && (
              <TextContent
                dangerouslySetInnerHTML={{
                  __html: description.childMarkdownRemark.html,
                }}
                style={{ whiteSpace: "pre-wrap" }}
              />
            )}
            {_arguments && (
              <ArgumentList align={"center"}>
                {_arguments.map((argument: any, index: number) => (
                  <Argument align={"center"} color="branding" key={index}>
                    <WebText
                      size="m"
                      color="branding"
                      style={{ fontWeight: "bold", display: "flex" }}
                    >
                      <Icon size={24} name="check-small" />
                      <span>{argument}</span>
                    </WebText>
                  </Argument>
                ))}
              </ArgumentList>
            )}
            {button && (
              <InlineContainer align={"center"}>
                <MobileContainer>
                  <ButtonElement link={button} size="l">
                    {button.label}
                  </ButtonElement>
                </MobileContainer>
              </InlineContainer>
            )}
          </Header>
          {image && !videoUrl && !imageAboveText && (
            <ImageContainer>
              <ImageElement
                gatsbyImageData={image.gatsbyImageData}
                style={{ width: "100%", height: "100%" }}
                alt={image.title || ""}
              />
            </ImageContainer>
          )}
          {image && videoUrl && (
            <VideoContainer>
              <VideoElement
                gatsbyImageData={image.gatsbyImageData}
                alt={image.title || ""}
                style={{
                  width: "100%",
                  height: "100%",
                }}
                videoUrl={videoUrl}
              />
            </VideoContainer>
          )}
        </Container>
      </Wrapper>
    </Background>
  );
};

export default SectionHeroBlue;

export const fragment = graphql`
  fragment SectionHeroBlueFragment on ContentfulSectionHeroBlue {
    title
    description {
      childMarkdownRemark {
        html
      }
    }
    image {
      gatsbyImageData(quality: 90, placeholder: NONE, layout: FULL_WIDTH)
      title
    }
    imageAboveText
    videoUrl
    button {
      ...LinkFragment
    }
    textArguments: arguments
    extraPaddingBottom
  }
`;

type BackgroundProps = {
  $extraPaddingBottom: boolean;
};

const Background = styled.div<BackgroundProps>`
  position: relative;
  background-color: ${color("background-primary")};
  ${(props: any) => {
    return props.$extraPaddingBottom
      ? css`
          padding-bottom: 10px;
          ${media.greaterThan("tablet")`
        padding-bottom: 150px;
        `}
        `
      : "";
  }}
`;

type BackgroundWrapperProps = {
  $fullHeight: boolean;
};

const BackgroundWrapper = styled.div<BackgroundWrapperProps>`
  position: absolute;
  height: 100%;
  width: 100%;
  > * {
    height: ${(props) => (props.$fullHeight ? "100%" : "calc(100% - 96px)")};
    width: 100%;
  }
  ${media.greaterThan("tablet")`
        height: 100%;
        > * {
          height: ${(props: any) =>
            props.$fullHeight ? "100%" : "calc(100% - 200px)"};
        }
    `}
`;

type WrapperProps = {
  $imageAboveText: boolean;
};

const Wrapper = styled.div<WrapperProps>`
  ${containerCss}
  padding: 6rem 1rem ${(props) => (props.$imageAboveText ? "2rem" : 0)} 1rem;
  z-index: 1;
  text-align: center;
  ${media.greaterThan("tablet")`
    height: auto;
    padding: ${(props: any) =>
      props.$imageAboveText ? "7rem" : "10rem"}  2rem ${(props: any) =>
    props.$imageAboveText ? "2rem" : 0} 2rem;
  `}
`;

type ContainerProps = {
  align?: Position;
};

const Container = styled.div<ContainerProps>`
  max-width: 796px;
  margin: 0;
  h1 {
    padding-bottom: 0.5rem;
  }
  ${(props) =>
    props.align === "left" &&
    css`
      max-width: 470px;
    `};

  ${media.greaterThan("tablet")`
    margin: auto;
    text-align: center;
    h1 {
        padding-bottom: 1rem;
    }
    ${(props: any) =>
      props.align === "left" &&
      css`
        text-align: left;
        margin: 0;
      `};
`}
`;

const Header = styled.div`
  max-width: 796px;
  margin: 0 auto;
`;

type InlineContainerProps = {
  align?: Position;
};

const InlineContainer = styled.div<InlineContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  ${(props) => css`
    ${media.greaterThan("tablet")`
      flex-direction: row;
      justify-content: ${props.align === "left" ? "flex-start" : "center"};
    `}
  `}
`;

type MobileContainerProps = {
  align?: Position;
};

const MobileContainer = styled.div<MobileContainerProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  ${(props) => css`
    ${media.greaterThan("tablet")`
      justify-content: ${props.align === "left" ? "flex-start" : "center"};
      align-items: center;
    `}
  `}
`;

type ArgumentListProps = {
  align?: Position;
};

const ArgumentList = styled.div<ArgumentListProps>`
  margin: auto auto 0.75rem auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  text-align: center;
  ${(props) => css`
    ${media.greaterThan("tablet")`
      display: flex;
      flex-direction: row;
      justify-content: ${props.align === "left" ? "flex-start" : "center"};
      margin: auto auto 1.5rem auto;
    `}
  `}
`;

type ArgumentProps = {
  align?: Position;
};

const Argument = styled.div<ArgumentProps>`
  padding-bottom: 0.5rem;
  margin: 0 0.25rem;

  ${(props) => css`
    ${media.greaterThan("tablet")`
      ${
        props.align === "left" &&
        css`
          margin: 0 1rem 0 0;
        `
      };
      padding-bottom: 0;
    `};
  `};
`;
const TextContent = styled.div`
  position: relative;
  p {
    margin-bottom: 12px;
    color: ${color("text-primary:10")};
    ${webtext("text", "l")};
  }
`;
const ImageContainer = styled.div`
  margin: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  height: auto;
  width: 100%;
  margin-top: 2rem;
  ${media.greaterThan("tablet")`
    width: 632px;
    margin-top: 2.5rem;
  `};
`;

const VideoContainer = styled.div`
  padding-bottom: 1rem;
  margin-top: 2rem;
  -webkit-filter: drop-shadow(0px 12px 8px rgba(0, 0, 0, 0.3));
  filter: drop-shadow(0px 12px 8px rgba(0, 0, 0, 0.3));
  ${media.greaterThan("tablet")`
    margin: 0 auto;
    width: 632px;
    margin-top: 2.5rem;
    padding-bottom: 1rem;
  `};
`;
